import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';
import type { Notification } from '../types';

export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.getNotifications(),
    refetchInterval: 30000,
  });

  const notifications: Notification[] = data?.data ?? [];
  const unreadCount = notifications.length;
  const displayNotifications = notifications.slice(0, 10);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (n: Notification) => {
    markReadMutation.mutate(n.id);
    if (n.ticket_id) {
      navigate(`/tickets/${n.ticket_id}`);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        aria-label={t('aria.notifications')}
        className="relative p-2 text-surface-500 hover:text-surface-700 transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-danger-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-surface-0 rounded-xl shadow-float border border-surface-400/15 z-50 max-h-96 overflow-hidden" dir="rtl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-400/10">
            <h3 className="text-sm font-bold text-surface-800">{t('notifications.title')}</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
              >
                <CheckCheck size={14} /> {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {(markReadMutation.isError || markAllReadMutation.isError) && (
            <div className="bg-danger-50 border-b border-danger-100 text-danger-700 px-4 py-2 text-xs">
              {markAllReadMutation.isError ? t('notifications.markAllReadError') : t('notifications.markReadError')}
            </div>
          )}

          <div className="overflow-y-auto max-h-72">
            {displayNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-surface-400">{t('notifications.noNotifications')}</div>
            ) : (
              displayNotifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className="w-full text-right px-4 py-3 hover:bg-surface-50 transition-colors border-b border-surface-50 last:border-0"
                >
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 mt-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-surface-800">
                        {t(`notifications.${n.event_type}`, { defaultValue: n.event_type })}
                      </p>
                      {n.ticket_title && (
                        <p className="text-xs text-surface-500 truncate">{n.ticket_title}</p>
                      )}
                      <p className="text-xs text-surface-400 mt-0.5">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: he })}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
